import { useState, useCallback, MutableRefObject } from 'react';
import {
  IMessage,
  MessageType,
  EAudioPlaybackType,
  AWAITING_RESPONSE_TYPE,
} from '@core/assistant/interfaces';
import {
  bedrockResultNotFoundReply,
  debugTagName,
} from '@core/assistant/constants';
import { useTranslation } from 'react-i18next';
import AssistantQueryService from '@services/AssistantQueryService';
import { assistantMapKey } from '@shared/utils/assistantMap';
import { shared } from './Shared';
import { log } from './Debug';
import { globals } from './globals';
import { formatSpotRegionName } from './Regions';
import {
  resolveDetectedPollyLanguage,
  selectVoiceForLanguage,
} from '@constants/polly';
import { GetEffectiveAiSettingsFn } from './useAssistantScripts';

export type GeneratePollyAudioFn = (
  text: string,
  engine?: string,
  voiceId?: string,
  language?: string,
  disableOptimizations?: boolean,
) => Promise<void>;

export type UseAssistantChatParams = {
  mytaverseEventRef: MutableRefObject<any>;
  activeBrandingIdRef: MutableRefObject<string | undefined>;
  generatePollyAudioRef: MutableRefObject<GeneratePollyAudioFn>;
  getEffectiveAiSettings: GetEffectiveAiSettingsFn;
  activeRegionNameRef: MutableRefObject<string | undefined>;
};

export const useAssistantChat = ({
  mytaverseEventRef,
  activeBrandingIdRef,
  generatePollyAudioRef,
  getEffectiveAiSettings,
  activeRegionNameRef,
}: UseAssistantChatParams) => {
  const { t: translate } = useTranslation('common');
  const [chatHistory, setChatHistory] = useState<IMessage[]>([]);
  const [awaitingResponseType, setAwaitingResponseType] = useState(
    AWAITING_RESPONSE_TYPE.NONE,
  );
  const [lastUserMessageData, setLastUserMessageData] = useState({
    eventId: '',
    participantId: '',
    timestamp: 0,
  });
  const [isDisplayUserFeedback, setIsDisplayUserFeedback] = useState(false);

  const addMessage = useCallback(
    ({ text, type, tag }: { text: string; type: MessageType; tag?: string }) => {
      setChatHistory((prevState) => {
        const brandingId = activeBrandingIdRef.current || undefined;
        return [
          ...prevState,
          { text, type, timestamp: Date.now(), brandingId, tag },
        ];
      });
    },
    [],
  );

  const handleSendRequest = useCallback(
    async (text: string) => {
      setIsDisplayUserFeedback(false);
      setAwaitingResponseType(AWAITING_RESPONSE_TYPE.SHORT);

      shared.audioPlayBackType = EAudioPlaybackType.CHAT;
      log('currentRegion', globals.currentRegion);

      const currentArea = activeRegionNameRef.current ?? (debugTagName || '');
      const tag = activeRegionNameRef.current || undefined;

      addMessage({ text, type: MessageType.USER, tag });

      log('useAudioZoneBasedContext currentRegion', currentArea);
      log(mytaverseEventRef.current?.currentEventId);

      const modelResponse = await AssistantQueryService.queryAssistant({
        text,
        tag: formatSpotRegionName(currentArea),
        eventId: mytaverseEventRef.current?.currentEventId || 'debug',
        includeScriptContext:
          mytaverseEventRef.current?.currentEventId || debugTagName
            ? true
            : false,
        mapName: assistantMapKey(mytaverseEventRef.current?.currentRoom?.map),
        brandingId:
          mytaverseEventRef.current?.currentRoom?.activeBrandingId ||
          undefined,
      });

      setAwaitingResponseType(AWAITING_RESPONSE_TYPE.NONE);

      if (modelResponse.text !== bedrockResultNotFoundReply) {
        const aiSettings = getEffectiveAiSettings();
        // Prefer the language the question was detected to be asked in so the
        // spoken reply matches it; fall back to the event's configured language
        // when detection is absent or the language has no Polly voice.
        const language =
          resolveDetectedPollyLanguage(modelResponse.language) ??
          aiSettings.language;
        const { engine, voiceId } = selectVoiceForLanguage(
          language,
          aiSettings.voicesByLanguage,
        );
        generatePollyAudioRef.current(
          modelResponse.text,
          engine,
          voiceId,
          language,
        );
        addMessage({ text: modelResponse.text, type: MessageType.ASSISTANT, tag });
      }

      if (
        modelResponse.text === bedrockResultNotFoundReply &&
        modelResponse.lastUserMessageData
      ) {
        addMessage({
          text: translate('assistant.resultNotFoundReply'),
          type: MessageType.ASSISTANT,
          tag,
        });
        setIsDisplayUserFeedback(true);
        setLastUserMessageData(modelResponse.lastUserMessageData);
      }

      log('useAudioZoneBasedContext modelResponse', modelResponse);
    },
    [addMessage, translate],
  );

  const handleSubmitUserFeedback = useCallback(
    async (isUserApproved: boolean) => {
      await AssistantQueryService.flagAssistantReply({
        eventId: lastUserMessageData.eventId,
        timestamp: lastUserMessageData.timestamp,
        isFeedbackRequired: isUserApproved ? 2 : 3,
      });
      setIsDisplayUserFeedback(false);

      if (isUserApproved) {
        addMessage({
          text: 'Your request is recorded',
          type: MessageType.ASSISTANT,
        });
      }
    },
    [lastUserMessageData, addMessage],
  );

  return {
    chatHistory,
    setChatHistory,
    awaitingResponseType,
    setAwaitingResponseType,
    lastUserMessageData,
    setLastUserMessageData,
    isDisplayUserFeedback,
    setIsDisplayUserFeedback,
    addMessage,
    handleSendRequest,
    handleSubmitUserFeedback,
  };
};
